import { useState, useEffect } from "react";
import Pagination from "../../../components/common/Pagination";
import SearchBox from "../../../components/common/SearchBox";
import { categoryApi } from "../../../api/categoryApi";
import { storageApi } from "../../../api/storageApi";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../../../components/common/ConfirmModal";
import LoadingOverlay from "../../../components/common/LoadingOverlay";
import AlertMessage from "../../../components/common/AlertMessage";
import ImageDetailsModal from "../../../components/common/ImageDetailsModal";
import { useLoading } from "../../../context/LoadingContext";
import "./CategoryService.css";
export default function CategoryService() {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(5);
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  //
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ type: "", message: "" });
  const [deleteId, setDeletedId] = useState(null);
  const [imageInfo, setImageInfo] = useState(null);
  const {showLoading, hideLoading} = useLoading();


  const [newCategory, setNewCategory] = useState({
    name: "",
    description: "",
    type: "",
    image: null
  });

  // Preview image and search/filter states must be top-level hooks
  const [previewImage, setPreviewImage] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [imageModal, setImageModal] = useState({
    show: false,
    imageUrl: "",
    title: "",
    subtitle: ""
  });

  // Fetch data from API and attach image URLs
  const fetchData = async (search) => {
    try {
      const params = {
        pageNumber,
        pageSize,
        filters: {},
        isSortDesc: false,
        sortKey: "createdAt",
        seachKey: search // keep as-is if API expects this field
      };
      showLoading();
      const json = await categoryApi.getTable(params);
      const items = json.success ? json.data.items : [];
      setData([...items])
      const total = json.data?.totalCount ?? items.length;
      const pSize = json.data?.pageSize ?? pageSize;
      setTotalPages(Math.max(1, Math.ceil(total / pSize)));
    } catch (err) {
      setTotalPages(1);
    }
    finally {
      hideLoading()
    }
  };

  // Load data when pageNumber or pageSize changes (or when filter/search should be applied server-side)
  useEffect(() => {
    const t = setTimeout(() => {
      fetchData(searchText);
    }, 400); // debounce 400ms
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, pageSize, searchText]);

  // Add category (simple client-side append)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!newCategory.name.trim()) return;
      const param = {
        "name": newCategory.name,
        "desc": newCategory.description,
        "fileId": imageInfo?.imageId,
        "fileName": imageInfo?.imageName
      };
      var isSuccess = false;
      showLoading()
      const createEntry = await categoryApi.create(param);
      if (createEntry.success) {
        isSuccess = true;
      }
      if (isSuccess) {
        setAlert({ type: "success", message: "Category added successfully!" });
      }
      else {
        setAlert({ type: "danger", message: "Failed to add category!" });
      }
      await fetchData();
    }
    catch {
      setAlert({ type: "danger", message: "Failed to add category!" });
    }
    finally {
      setLoading(false);
      setNewCategory({ name: "", description: "", type: "", image: null });
      setPreviewImage(null);
      setImageInfo(null);
      hideLoading()
    }
  };

  // Preview image
  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const form = new FormData();
      form.append("file", file);

      var res = await storageApi.uploadTemptFile(form);
      if (res.success) {
        const data = res.data;
        const url = data.url;
        setNewCategory((prev) => ({ ...prev, image: file, imgUrl: url }));
        setPreviewImage(url)
        setImageInfo({imageId: data.id, imageUrl: url, imageName: file.name})
      }
    }
  };
  const handleDelete = async () => {
    setConfirmDelete(false);
    setLoading(true);
    try {
      await categoryApi.remove(deleteId);
      setAlert({ type: "success", message: "Deleted successfully!" });
      fetchData();
    } catch (err) {
      setAlert({ type: "danger", message: "Failed to delete!" });
    }
    setDeletedId(null);
    setLoading(false);
  };
  const navigate = useNavigate();
  return (
    <div className="category-page">
      <ConfirmModal
        show={confirmDelete}
        title="Delete Category"
        message="Are you sure you want to delete this category?"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      />

      <LoadingOverlay active={loading} />

      <AlertMessage
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert({ type: "", message: "" })}
      />

      <ImageDetailsModal
        show={imageModal.show}
        imageUrl={imageModal.imageUrl}
        title={imageModal.title}
        subtitle={imageModal.subtitle}
        onClose={() => setImageModal((prev) => ({ ...prev, show: false }))}
      />

      <div className="category-header">
        <div>
          <h2 className="mb-1 fw-bold">Category Management</h2>
          <p className="category-subtitle">Manage category list and images without changing data logic.</p>
        </div>
      </div>

      {/* SEARCH + FILTER */}
      <div className="category-card">
        <div className="row g-3">
          <div className="col-md-6">
            <SearchBox
              value={searchText}
              onChange={(val) => {
                setSearchText(val);
              }}
              placeholder="Search category..."
            />
          </div>
        </div>
      </div>

      {/* CATEGORY LIST */}
      <div className="category-card">
        <table className="table table-hover align-middle mb-0">
          <thead>
            <tr>
              <th style={{ width: "150px" }}>Image</th>
              <th>Name</th>
              <th>Description</th>
              <th style={{ width: "80px" }}></th>
            </tr>
          </thead>

          <tbody>
            {data.length === 0 && (
              <tr>
                <td colSpan="4" className="text-center text-muted py-4">
                  No categories found.
                </td>
              </tr>
            )}

            {data.map(cat => (
              <tr key={cat.id}>
                <td>
                  {(cat.urlImage || cat.image) ? (
                    <button
                      type="button"
                      className="category-thumb-btn"
                      onClick={() => setImageModal({
                        show: true,
                        imageUrl: cat.urlImage || cat.image,
                        title: cat.name || "Category image",
                        subtitle: cat.desc || ""
                      })}
                    >
                      <img src={cat.urlImage || cat.image} alt={cat.name || "category"} />
                    </button>
                  ) : (
                    <div className="category-thumb-empty">No image</div>
                  )}
                </td>

                <td className="fw-bold">{cat.name}</td>
                <td>{cat.desc}</td>

                <td className="text-end">
                  <button
                    className="btn btn-sm btn-light me-2"
                    onClick={() =>
                      navigate(`./${cat.id}`, {
                        state: {
                          name: cat.name,
                          type: cat.type,
                          description: cat.description,
                          imageUrl: cat.urlImage,
                          fileId: cat.fileId
                        }
                      })
                    }
                  >
                    <i className="bi bi-pencil"></i>
                  </button>

                  <button className="btn btn-sm btn-outline-danger me-2" onClick={() => {
                    setConfirmDelete(true);
                    setDeletedId(cat.id);
                  }}>
                    <i className="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>


      {/* PAGINATION */}
      <Pagination
        currentPage={pageNumber}
        totalPages={totalPages}
        onChange={setPageNumber}
      />

      <hr className="my-5" />

      {/* ADD NEW CATEGORY */}
      <h4 className="fw-semibold mb-3">Add new category</h4>

      <div className="category-card">
        <form className="category-add-grid" onSubmit={handleSubmit}>
          <div>
            <label className="form-label">Category name</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter name"
              value={newCategory.name}
              onChange={(e) =>
                setNewCategory({ ...newCategory, name: e.target.value })
              }
            />
          </div>

          <div>
            <label className="form-label">Description</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter description"
              value={newCategory.description}
              onChange={(e) =>
                setNewCategory({ ...newCategory, description: e.target.value })
              }
            />
          </div>

          <div className="category-add-full">
            <label className="form-label">Image</label>
            <div className="d-flex justify-content-start align-items-center gap-2">
              <input
                type="file"
                className="form-control"
                accept="image/*"
                onChange={handleImageChange}
              />

              <button className="btn btn-primary">Auto gen</button>
            </div>
          </div>

          {previewImage && (
            <div className="category-add-full">
              <button
                type="button"
                className="category-thumb-btn"
                onClick={() => setImageModal({
                  show: true,
                  imageUrl: previewImage,
                  title: newCategory.name || "New category preview",
                  subtitle: newCategory.description || ""
                })}
              >
                <img src={previewImage} alt="preview" />
              </button>
            </div>
          )}

          <div className="category-add-full">
            <button type="submit" className="btn btn-success">
              Add Category
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
